import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './Pages.css';

const emptyForm = { name: '', description: '', duration_minutes: 60, price: '', visibility_status: 'visible', recordable: false };

const PractitionerServices = ({ user, apiUrl, token }) => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const headers = { Authorization: `Bearer ${token}` };

  const userRole = user?.role || JSON.parse(localStorage.getItem('user') || '{}')?.role;

  useEffect(() => {
    if (userRole !== 'practitioner' && userRole !== 'admin') {
      navigate('/');
      return;
    }
    fetchServices();
  }, [userRole, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/services`, { headers });
      setServices(response.data);
    } catch (err) {
      setError('Failed to fetch services');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowForm(true);
  };

  const openEdit = (service) => {
    setEditingId(service.id);
    setFormData({
      name: service.name,
      description: service.description || '',
      duration_minutes: service.duration_minutes || 60,
      price: service.price,
      visibility_status: service.visibility_status,
      recordable: service.recordable === true
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingId) {
        await axios.patch(`${apiUrl}/services/${editingId}`, formData, { headers });
      } else {
        await axios.post(`${apiUrl}/services`, formData, { headers });
      }
      setShowForm(false);
      fetchServices();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save service');
    }
  };

  const handleDelete = async (serviceId) => {
    setError('');
    try {
      await axios.delete(`${apiUrl}/services/${serviceId}`, { headers });
      fetchServices();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete service');
    }
  };

  const handleVisibilityChange = async (serviceId, newStatus) => {
    setError('');
    try {
      await axios.patch(`${apiUrl}/services/${serviceId}`, { visibility_status: newStatus }, { headers });
      fetchServices();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update service');
    }
  };

  if (loading) return <div className="page-container">Loading services...</div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>My Services</h1>
        <div className="flex-between">
          <button className="btn btn-secondary" onClick={() => navigate('/practitioner/dashboard')}>Back to Dashboard</button>
          <button className="btn btn-primary" onClick={openCreate}>+ New Service</button>
        </div>
      </div>
      {error && <div className="error-message">{error}</div>}

      <div className="list-container table-wrap">
        <table className="table">
          <thead>
            <tr><th>Service</th><th>Duration</th><th>Price</th><th>Recordable</th><th>Visibility</th><th></th></tr>
          </thead>
          <tbody>
            {services.map((s) => (
              <tr key={s.id}>
                <td>
                  <strong>{s.name}</strong>
                  {s.description && <div className="text-muted" style={{ fontSize: '0.85rem', marginTop: 2 }}>{s.description}</div>}
                </td>
                <td>{s.duration_minutes ? `${s.duration_minutes} min` : <span className="text-muted">—</span>}</td>
                <td>${parseFloat(s.price).toFixed(2)}</td>
                <td>{s.recordable ? <span className="badge badge-info">Recordable</span> : <span className="text-muted">—</span>}</td>
                <td>
                  <select
                    value={s.visibility_status}
                    onChange={(e) => handleVisibilityChange(s.id, e.target.value)}
                  >
                    <option value="visible">Visible</option>
                    <option value="hidden">Hidden</option>
                    <option value="special_pricing">Special Pricing</option>
                  </select>
                </td>
                <td>
                  <button className="btn btn-secondary btn-sm" onClick={() => openEdit(s)} style={{ marginRight: 6 }}>Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(s.id)}>Delete</button>
                </td>
              </tr>
            ))}
            {services.length === 0 && <tr><td colSpan="6" className="empty-state">No services yet — create your first one above.</td></tr>}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="modal" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingId ? 'Edit Service' : 'New Service'}</h2>
              <button className="close-btn" onClick={() => setShowForm(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Service Name</label>
                <input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea rows="3" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Duration (minutes)</label>
                  <input type="number" value={formData.duration_minutes} onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Price ($)</label>
                  <input type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} required />
                </div>
              </div>
              <div className="form-group">
                <label>Visibility</label>
                <select value={formData.visibility_status} onChange={(e) => setFormData({ ...formData, visibility_status: e.target.value })}>
                  <option value="visible">Visible</option>
                  <option value="hidden">Hidden</option>
                  <option value="special_pricing">Special Pricing</option>
                </select>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontSize: '0.9rem' }}>
                <input
                  type="checkbox"
                  checked={formData.recordable}
                  onChange={(e) => setFormData({ ...formData, recordable: e.target.checked })}
                />
                Allow clients to opt in to having this session recorded
              </label>
              <button type="submit" className="btn btn-primary btn-block">{editingId ? 'Save Changes' : 'Create Service'}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PractitionerServices;
